import React, { useState } from 'react';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { CalculationResult } from '../../types';
import { colorSystem } from '../../utils/colorSystem';
import { ChartToggle } from './ChartToggle';
import { formatAxisValue, formatTooltipValue } from '../../utils/formatters';

interface TargetProductivityVsEPTChartProps {
  results: CalculationResult[];
}

// Utility function to truncate Y-axis labels
const truncateLabel = (label: string, maxLength: number = 16): string => {
  if (label.length <= maxLength) return label;
  return label.substring(0, maxLength - 3) + '...';
};

export function TargetProductivityVsEPTChart({ results }: TargetProductivityVsEPTChartProps) {
  const [showSecondaryAxis, setShowSecondaryAxis] = useState(true);

  // Filter for Known Experiences mode results only
  const knownExperiencesResults = results.filter(r => r.knownExperiencesMode);

  const chartData = React.useMemo(() => {
    colorSystem.reset();
    
    const dataMap = new Map<string, { [key: string]: any }>();
    
    knownExperiencesResults.forEach(result => {
      const date = result.calculationDate;
      if (!dataMap.has(date)) {
        dataMap.set(date, {
          date,
          ept: result.ept,
          values: {}
        });
      }
      const roleKey = `${result.role}-${result.experience}`;
      dataMap.get(date)!.values[roleKey] = result.theoreticalProductivityPercentage || 0;
    });

    const data = Array.from(dataMap.entries())
      .map(([date, values]) => ({
        date,
        ...values
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const roleKeys = Array.from(new Set(
      knownExperiencesResults.map(r => `${r.role}-${r.experience}`)
    ));

    return { data, roleKeys };
  }, [knownExperiencesResults]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-medium text-gray-900 mb-2">
          {new Date(label).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </p>
        {payload.map((entry: any) => {
          if (entry.dataKey === 'ept') {
            return (
              <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-gray-700">EPT:</span>
                <span className="font-medium">{formatTooltipValue(entry.value, '', 's')}</span>
              </div>
            );
          }

          return (
            <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-gray-700">{entry.name}:</span>
              <span className="font-medium">{formatTooltipValue(entry.value, '', '%')}</span>
            </div>
          );
        })}
      </div>
    );
  };

  if (knownExperiencesResults.length === 0) {
    return (
      <div className="w-full h-[400px] bg-white p-6 rounded-xl shadow-sm chart-container flex items-center justify-center">
        <p className="text-gray-500">No Known Experiences data available for this chart.</p>
      </div>
    );
  }

  return (
    <div className="w-full h-[600px] bg-white p-6 rounded-xl shadow-sm chart-container">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">EPT vs Target Productivity Analysis</h3>
          <p className="text-sm text-gray-500">
            EPT performance (bars) and target productivity percentage (lines) over time
          </p>
        </div>
        <ChartToggle
          showSecondaryAxis={showSecondaryAxis}
          onToggle={setShowSecondaryAxis}
          label="Show Productivity %"
        />
      </div>
      
      <ResponsiveContainer width="100%" height="87%">
        <ComposedChart 
          data={chartData.data}
          margin={{ top: 60, right: 30, left: 20, bottom: 60 }}
        >
          <defs>
            <linearGradient id="eptBarsGradient" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3}/>
              <stop offset="100%" stopColor="#1D4ED8" stopOpacity={0.8}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            label={{ 
              value: 'Analysis Date', 
              position: 'bottom', 
              offset: 40,
              style: { fontWeight: 'bold' }
            }}
            tick={{ dy: 10 }}
          />
          <YAxis
            yAxisId="left"
            width={100}
            domain={[0, 6]}
            label={{ 
              value: truncateLabel('EPT (seconds)'), 
              angle: 0,
              position: 'top',
              dy: -30,
              textAnchor: 'start',
              dx: 50,
              style: { fontWeight: 'bold' }
            }}
            tickFormatter={formatAxisValue}
          />
          {showSecondaryAxis && (
            <YAxis
              yAxisId="right"
              orientation="right"
              width={100}
              domain={[0, 100]}
              label={{
                value: truncateLabel('Theoretical Productivity (%)'),
                angle: 0,
                position: 'top',
                dy: -30,
                textAnchor: 'end',
                dx: -50,
                style: { fontWeight: 'bold' }
              }}
              tickFormatter={formatAxisValue}
            />
          )}
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="bottom" 
            height={36}
            wrapperStyle={{
              paddingTop: '20px',
              marginBottom: '-45px'
            }}
          />
          <Bar
            yAxisId="left"
            dataKey="ept"
            name="EPT"
            fill="url(#eptBarsGradient)"
            opacity={0.7}
            barSize={30}
          />
          {showSecondaryAxis && chartData.roleKeys.map(roleKey => {
            const [role, experience] = roleKey.split('-');
            const lineName = "Theoretical Productivity";
            
            return (
              <Line
                key={roleKey}
                yAxisId="right"
                type="monotone"
                dataKey={`values.${roleKey}`}
                name={lineName}
                stroke={colorSystem.getColor(role, experience)}
                strokeWidth={3}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
                connectNulls={true}
              />
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}